// MP219 — Metric / KPI tile primitive.
//
// Formalises the pattern of "small icon + tiny uppercase label +
// big number + small subtitle line + optional trend indicator"
// that appears as bespoke variants across EmailSecurityPage,
// InsiderPage, ExternalRiskPage, and the Harness Analytics page in
// the internal consoles. apps/web/src/components/shell/AppDashboardKpiTile
// is one concrete realisation of this; this primitive lifts it into
// the shared package so internal consoles can use the same shape.

import * as React from 'react';
import { cn } from '../lib/cn';

export interface MetricTileProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Lucide icon component, instantiated by caller. */
  icon?: React.ReactNode;
  /** Uppercase tiny label above the value ("Open incidents"). */
  label: React.ReactNode;
  /** Large primary number / text. */
  value: React.ReactNode;
  /** Optional supporting text below the value ("3 in 24h · 8 in 7d"). */
  subtitle?: React.ReactNode;
  /** Optional trend / status node (TrendArrow, sparkline, pill). */
  trend?: React.ReactNode;
  /** Visual size. `sm` for dense dashboards, `lg` for the splash. */
  size?: 'sm' | 'md' | 'lg';
}

export const MetricTile = React.forwardRef<HTMLDivElement, MetricTileProps>(
  (
    { className, icon, label, value, subtitle, trend, size = 'md', ...props },
    ref,
  ) => {
    const valueClass =
      size === 'sm'
        ? 'text-lg'
        : size === 'lg'
          ? 'text-3xl'
          : 'text-2xl';
    return (
      <div
        ref={ref}
        className={cn(
          'rounded-lg border bg-card p-3 shadow-sm',
          className,
        )}
        {...props}
      >
        <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {icon && <span className="text-current">{icon}</span>}
          <span>{label}</span>
          {trend && <span className="ml-auto">{trend}</span>}
        </div>
        <div className={cn('mt-1 font-bold tabular-nums', valueClass)}>{value}</div>
        {subtitle && (
          <div className="mt-0.5 text-[10px] text-muted-foreground">{subtitle}</div>
        )}
      </div>
    );
  },
);
MetricTile.displayName = 'MetricTile';
